/**
 * Outcome — the result of one act, rendered WITHOUT PARAPHRASE.
 *
 * The refusals on this surface are the product. `publish_global_knowledge_version`
 * says "may assist discovery but may not become an external requirement";
 * `retire_global_knowledge_version` says "is already retired; a version leaves
 * force once". Those sentences name the wall that stopped the operator. A
 * console that replaced them with "Could not publish" would delete the only
 * part of the answer that tells them what to do next — and this whole domain's
 * design is that the message names the wall.
 *
 * So the server's message is rendered verbatim, and the console adds only the
 * KIND of answer it is, because those four are what an operator confuses.
 */
import type { ConsoleOutcome } from '../platform/client.js';

export interface ActResult {
  label: string;
  outcome: ConsoleOutcome<unknown>;
}

export function Outcome({ result }: { result: ActResult | null }) {
  if (!result) return null;
  const { label, outcome } = result;

  switch (outcome.kind) {
    case 'ok':
      return (
        <p role="status">
          <strong>{label} recorded.</strong>
        </p>
      );
    case 'absent':
      return (
        <div className="banner" role="alert">
          <strong>{label} — not found.</strong> {outcome.message}
        </div>
      );
    case 'unauthenticated':
      return (
        <div className="banner" role="alert">
          <strong>{label} — the platform credential was not accepted.</strong> {outcome.message}
        </div>
      );
    case 'invalid':
      return (
        <div className="banner" role="alert">
          <strong>{label} — the request was malformed.</strong> {outcome.message}
          {outcome.issues.length > 0 ? (
            <ul>
              {outcome.issues.map((issue) => (
                <li key={`${issue.path}:${issue.message}`}>
                  {issue.path}: {issue.message}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      );
    case 'refused':
      return (
        <div className="banner" role="alert">
          <strong>
            {label} refused ({outcome.status}).
          </strong>
          {/* VERBATIM. See the docblock. */}
          <pre>{outcome.message}</pre>
        </div>
      );
    case 'transport':
      return (
        <div className="banner" role="alert">
          <strong>{label} — the platform API could not be reached.</strong> {outcome.message} The
          act was NOT recorded, and nothing here should be read as a result.
        </div>
      );
  }
}
